// AIManager.js - Handles all AI-related functionality
import { getProviderName as resolveProviderName, getErrorMessage, generateReplicateImage, streamChatCompletion, resolveModelCredentials } from './aiService.js';

const MODE_AUTOMANAGED = 'automanaged';
const MODE_API = 'api';
const DEFAULT_SYSTEM_PROMPT = 'You are a helpful AI assistant that generates creative and relevant ideas.';

const MODE_STORAGE_KEYS = {
    [MODE_AUTOMANAGED]: {
        models: 'ai_models_automanaged',
        activeModels: 'ai_active_models_automanaged',
        systemPrompt: 'ai_system_prompt_automanaged',
        modelPrompts: 'ai_model_system_prompts_automanaged'
    },
    [MODE_API]: {
        models: 'ai_models_api',
        activeModels: 'ai_active_models_api',
        systemPrompt: 'ai_system_prompt_api',
        modelPrompts: 'ai_model_system_prompts_api'
    }
};

export class AIManager {
    constructor(canvasState, uiManager) {
        this.canvasState = canvasState;
        this.uiManager = uiManager;
        
        // AI configuration
        const storedApiKey = localStorage.getItem('ai_api_key');
        const storedBaseUrl = localStorage.getItem('ai_base_url');
        const storedSecondaryApiKey = localStorage.getItem('ai_secondary_api_key');
        const storedSecondaryBaseUrl = localStorage.getItem('ai_secondary_base_url');

        this.apiKey = storedApiKey ? storedApiKey.trim() : '';
        this.baseURL = storedBaseUrl ? storedBaseUrl.trim() : '';
        this.secondaryApiKey = storedSecondaryApiKey ? storedSecondaryApiKey.trim() : '';
        this.secondaryBaseURL = storedSecondaryBaseUrl ? storedSecondaryBaseUrl.trim() : '';
        const storedReplicateKey = localStorage.getItem('replicate_api_key');
        this.replicateApiKey = storedReplicateKey ? storedReplicateKey.trim() : '';
        this.automanaged = localStorage.getItem('ai_automanaged') !== 'false';
        this.automanagedModelLookup = {};
        this.automanagedFetchedModels = [];
        this.automanagedCustomModels = localStorage.getItem('ai_automanaged_custom_models') === 'true';
        
        // Default models for API key mode (user-configurable)
        this.defaultApiModels = 'google/gemini-2.5-pro,anthropic/claude-sonnet-4.5';
        
        // Mode-specific configuration state
        this.modeState = {
            [MODE_AUTOMANAGED]: this.loadModeState(MODE_AUTOMANAGED),
            [MODE_API]: this.loadModeState(MODE_API)
        };
        
        // Request tracking for automanaged mode
        this.requestsRemaining = null;
        this.requestsLimit = null;
        this.sessionId = this.getOrCreateSessionId();
        
        // Fetch automanaged models if in automanaged mode (async, non-blocking)
        if (this.automanaged) {
            this.fetchAutomanagedModels().catch(err => {
                console.warn('Failed to fetch automanaged models on init:', err);
            });
        }
        this.modelPromptOverrides = {};
        this.legacyModelPrompts = {};
        this.clearedModelPromptKeys = new Set();
        this.modelSystemPrompts = {};
        
        const currentMode = this.getCurrentModeKey();
        this.applyModeState(currentMode);
        
        // Image model configuration
        this.defaultImageModels = 'recraft-ai/recraft-v3,google/imagen-4,bytedance/seedream-3';
        this.imageModels = localStorage.getItem('image_models') || this.defaultImageModels;
        this.activeImageModels = JSON.parse(localStorage.getItem('image_active_models') || '{}');
        console.log('Active image models on load:', this.activeImageModels);
        
        // Ensure default image models are active by default
        this.defaultImageModels.split(',').map(m => m.trim()).forEach(model => {
            if (!(model in this.activeImageModels)) {
                this.activeImageModels[model] = true;
            }
        });

        // Note: Generation states are now tracked per-node instead of globally
        // This allows simultaneous AI operations on different nodes
    }
    
    getDefaultSystemPrompt() {
        return DEFAULT_SYSTEM_PROMPT;
    }

    getCurrentModeKey() {
        return this.automanaged ? MODE_AUTOMANAGED : MODE_API;
    }

    normalizeModeKey(mode) {
        return mode === MODE_AUTOMANAGED ? MODE_AUTOMANAGED : MODE_API;
    }

    getModeStorageKeys(mode) {
        const normalized = this.normalizeModeKey(mode);
        return MODE_STORAGE_KEYS[normalized];
    }

    createEmptyModeState(mode) {
        const normalized = this.normalizeModeKey(mode);
        return {
            aiModels: normalized === MODE_API ? this.defaultApiModels : '',
            activeModels: {},
            systemPrompt: DEFAULT_SYSTEM_PROMPT,
            modelSystemPrompts: {}
        };
    }

    loadModeState(mode) {
        const normalized = this.normalizeModeKey(mode);
        const storageKeys = this.getModeStorageKeys(normalized);
        const state = this.createEmptyModeState(normalized);

        const modelsValue = localStorage.getItem(storageKeys.models);
        if (typeof modelsValue === 'string') {
            state.aiModels = modelsValue;
        }

        const activeModelsRaw = localStorage.getItem(storageKeys.activeModels);
        if (activeModelsRaw) {
            try {
                const parsedActive = JSON.parse(activeModelsRaw);
                if (parsedActive && typeof parsedActive === 'object' && !Array.isArray(parsedActive)) {
                    state.activeModels = parsedActive;
                }
            } catch (error) {
                console.warn(`Unable to parse stored active models for ${normalized}:`, error);
            }
        }

        const systemPromptValue = localStorage.getItem(storageKeys.systemPrompt);
        if (typeof systemPromptValue === 'string' && systemPromptValue.trim().length > 0) {
            state.systemPrompt = systemPromptValue;
        }

        const modelPromptsRaw = localStorage.getItem(storageKeys.modelPrompts);
        if (modelPromptsRaw) {
            try {
                const parsedPrompts = JSON.parse(modelPromptsRaw);
                if (parsedPrompts && typeof parsedPrompts === 'object' && !Array.isArray(parsedPrompts)) {
                    state.modelSystemPrompts = parsedPrompts;
                }
            } catch (error) {
                console.warn(`Unable to parse stored model prompts for ${normalized}:`, error);
            }
        }

        return state;
    }

    cloneModeState(mode) {
        const normalized = this.normalizeModeKey(mode);
        const source = this.modeState?.[normalized] || this.createEmptyModeState(normalized);
        return {
            aiModels: typeof source.aiModels === 'string' ? source.aiModels : '',
            activeModels: source.activeModels ? { ...source.activeModels } : {},
            systemPrompt: typeof source.systemPrompt === 'string'
                ? source.systemPrompt
                : DEFAULT_SYSTEM_PROMPT,
            modelSystemPrompts: source.modelSystemPrompts
                ? JSON.parse(JSON.stringify(source.modelSystemPrompts))
                : {}
        };
    }

    persistModeState(mode) {
        const normalized = this.normalizeModeKey(mode);
        const storageKeys = this.getModeStorageKeys(normalized);
        const state = this.modeState?.[normalized] || this.createEmptyModeState(normalized);

        try {
            localStorage.setItem(storageKeys.models, state.aiModels || '');
            localStorage.setItem(storageKeys.systemPrompt, typeof state.systemPrompt === 'string'
                ? state.systemPrompt
                : DEFAULT_SYSTEM_PROMPT);
            
            if (state.activeModels && Object.keys(state.activeModels).length > 0) {
                localStorage.setItem(storageKeys.activeModels, JSON.stringify(state.activeModels));
            } else {
                localStorage.removeItem(storageKeys.activeModels);
            }

            if (state.modelSystemPrompts && Object.keys(state.modelSystemPrompts).length > 0) {
                localStorage.setItem(storageKeys.modelPrompts, JSON.stringify(state.modelSystemPrompts));
            } else {
                localStorage.removeItem(storageKeys.modelPrompts);
            }
        } catch (error) {
            console.warn(`Unable to persist configuration for ${normalized} mode:`, error);
        }
    }

    applyModeState(mode) {
        const normalized = this.normalizeModeKey(mode);
        const state = this.cloneModeState(normalized);

        this.aiModels = state.aiModels || '';
        this.activeModels = state.activeModels || {};
        this.systemPrompt = typeof state.systemPrompt === 'string'
            ? state.systemPrompt
            : DEFAULT_SYSTEM_PROMPT;

        this.modelPromptOverrides = {};
        this.legacyModelPrompts = {};
        this.clearedModelPromptKeys = new Set();
        this.modelSystemPrompts = {};

        this.loadModelPromptConfig(state.modelSystemPrompts);
        this.updateModelPromptStorage({ persist: false, mode: normalized });
    }

    getStoredModeConfig(mode) {
        return this.cloneModeState(mode);
    }

    updateModeState(mode, updates = {}) {
        const normalized = this.normalizeModeKey(mode);
        const existing = this.cloneModeState(normalized);
        const nextState = {
            ...existing,
            ...updates
        };
        this.modeState[normalized] = nextState;
        this.persistModeState(normalized);
        return this.cloneModeState(normalized);
    }

    // Session management for guest request tracking
    getOrCreateSessionId() {
        let sessionId = localStorage.getItem('ai_session_id');
        if (!sessionId) {
            sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substring(7);
            localStorage.setItem('ai_session_id', sessionId);
        }
        return sessionId;
    }
    
    // Check remaining requests for automanaged mode
    async checkRequestLimits() {
        if (!this.automanaged) {
            return { hasRequests: true }; // No limits in API key mode
        }
        
        try {
            const userId = this.canvasState.authManager?.currentUser?.id || null;
            const sessionId = userId ? null : this.sessionId;
            
            const response = await fetch('/api/automanaged/check-limits', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, sessionId })
            });
            
            if (!response.ok) {
                console.error('Failed to check request limits');
                return { hasRequests: true }; // Fail open
            }
            
            const limits = await response.json();
            this.requestsRemaining = limits.remaining;
            this.requestsLimit = limits.limit;
            
            return limits;
        } catch (error) {
            console.error('Error checking request limits:', error);
            return { hasRequests: true }; // Fail open
        }
    }

    /**
     * Fetch available models from automanaged backend
     */
    async fetchAutomanagedModels() {
        if (!this.automanaged) {
            return; // Only fetch when in automanaged mode
        }
        
        try {
            const { fetchAutomanagedModels } = await import('./aiService.js');
            const data = await fetchAutomanagedModels();
            
            if (Array.isArray(data?.models)) {
                this.automanagedModelLookup = data.models.reduce((lookup, model) => {
                    if (model && typeof model.id === 'string') {
                        lookup[model.id] = model;
                    }
                    return lookup;
                }, {});

                const sanitizedIds = data.models
                    .map(model => (model && typeof model.id === 'string') ? model.id.trim() : '')
                    .filter(id => id.length > 0);

                this.automanagedFetchedModels = sanitizedIds;

                const hasStoredModels = typeof this.aiModels === 'string' && this.aiModels.trim().length > 0;
                const normalizedCurrentModels = this.normalizeModelList(this.aiModels);
                const matchesFetchedDefaults = sanitizedIds.length > 0
                    && normalizedCurrentModels.length === sanitizedIds.length
                    && normalizedCurrentModels.every((model, index) => model === sanitizedIds[index]);

                let effectiveModels = normalizedCurrentModels;

                if (!hasStoredModels || !this.automanagedCustomModels) {
                    this.aiModels = sanitizedIds.join(',');
                    effectiveModels = sanitizedIds;
                    this.automanagedCustomModels = false;
                    localStorage.removeItem('ai_automanaged_custom_models');
                } else if (matchesFetchedDefaults) {
                    this.aiModels = sanitizedIds.join(',');
                    effectiveModels = sanitizedIds;
                    this.automanagedCustomModels = false;
                    localStorage.removeItem('ai_automanaged_custom_models');
                } else {
                    effectiveModels = normalizedCurrentModels;
                }

                const previousActiveModels = this.activeModels && typeof this.activeModels === 'object'
                    ? this.activeModels
                    : {};
                const nextActiveModels = { ...previousActiveModels };

                effectiveModels.forEach((modelId) => {
                    const storedValue = Object.prototype.hasOwnProperty.call(previousActiveModels, modelId)
                        ? previousActiveModels[modelId]
                        : undefined;
                    nextActiveModels[modelId] = storedValue !== false;
                });

                delete nextActiveModels.automanaged;
                delete nextActiveModels['automanaged__0'];

                this.activeModels = nextActiveModels;
                this.updateModeState(MODE_AUTOMANAGED, {
                    aiModels: this.aiModels,
                    activeModels: this.activeModels
                });

                if (this.uiManager && typeof this.uiManager.updateModelSelectorUI === 'function') {
                    this.uiManager.updateModelSelectorUI();
                }

                if (this.uiManager && typeof this.uiManager.updateAutomanagedModelSummary === 'function') {
                    this.uiManager.updateAutomanagedModelSummary(data.models, data.defaultModel);
                }

                console.log('📊 Fetched automanaged models:', data.models.map(m => m.name).join(', '));
            }
        } catch (error) {
            console.error('Error fetching automanaged models:', error);
            // Keep existing models if fetch fails
        }
    }
    
    normalizeModelList(modelsString) {
        if (typeof modelsString !== 'string') {
            return [];
        }

        return modelsString
            .split(',')
            .map(model => model.trim())
            .filter(model => model.length > 0);
    }
    
    // AI text generation
    async generateAI() {
        if (!this.automanaged && !this.hasAnyTextProvider()) {
            this.uiManager.showApiKeyModal();
            return;
        }
        
        // Check request limits in automanaged mode
        if (this.automanaged) {
            const limits = await this.checkRequestLimits();
            if (!limits.hasRequests) {
                const isGuest = !this.canvasState.authManager?.currentUser;
                this.uiManager.showRequestLimitReached(isGuest);
                return;
            }
        }

        if (this.canvasState.selectedNodes.length === 0) {
            this.uiManager.showNotification('Please select a node first to generate connected ideas', 'warning');
            return;
        }

        if (this.canvasState.selectedNodes.length > 1) {
            this.uiManager.showNotification('Please select only one node to generate ideas. AI generation works with single nodes.', 'warning');
            return;
        }

        const sourceNode = this.canvasState.selectedNode;

        if (sourceNode.isGeneratingAI) {
            this.uiManager.showNotification('AI generation already in progress for this node...', 'info');
            return;
        }

        try {
            sourceNode.isGeneratingAI = true;
            this.uiManager.updateGenerateIdeasTooltip();

            console.log('🎯 Generating ideas for:', sourceNode.text);

            const ancestorNodes = this.canvasState.getAncestorNodes(sourceNode);
            const modelDescriptors = this.getActiveTextModelDescriptors();

            if (modelDescriptors.length === 0) {
                this.uiManager.showNotification('No active models selected. Please configure your AI models first.', 'warning');
                return;
            }

            this.canvasState.saveState();

            const nodeStillExists = this.canvasState.nodes.find(n => n.id === sourceNode.id);
            if (!nodeStillExists) {
                throw new Error('Source node was deleted during AI generation');
            }

            this.uiManager.showProgressNotification(
                `Starting AI generation with ${modelDescriptors.length} model(s)...`,
                modelDescriptors
            );

            let completedModels = 0;
            let successfulNodes = 0;
            const modelNodeMap = new Map();

            const formatPreview = (value) => {
                if (typeof value !== 'string') {
                    return '';
                }
                const normalized = value.replace(/\s+/g, ' ').trim();
                if (normalized.length > 160) {
                    return `${normalized.slice(0, 157)}…`;
                }
                return normalized;
            };

            const updateNodeContent = (node, content, { done = false } = {}) => {
                if (!node) return;
                node.text = content;
                node.isStreaming = !done;
                const baseHeight = 140;
                const extraHeight = Math.max(0, content.length - 220) * 0.35;
                node.height = Math.min(baseHeight + extraHeight, 520);
            };

            const childSpacing = 420;
            const startOffset = ((modelDescriptors.length - 1) / 2) * childSpacing;
            const baseY = sourceNode.y + sourceNode.height + 150;
            const sourceNodeId = sourceNode.id ? sourceNode.id.toString() : null;
            const nodeLookup = new Map(
                this.canvasState.nodes
                    .filter(node => node && node.id)
                    .map(node => [node.id.toString(), node])
            );
            const existingChildren = sourceNodeId
                ? this.canvasState.connections
                      .filter(connection => {
                          const fromId = connection.fromNode || connection.from;
                          return fromId && fromId.toString() === sourceNodeId;
                      })
                      .map(connection => {
                          const childId = connection.toNode || connection.to;
                          return childId ? nodeLookup.get(childId.toString()) : null;
                      })
                      .filter(Boolean)
                : [];
            const existingMaxRight = existingChildren.length > 0
                ? existingChildren.reduce(
                      (max, child) => {
                          const childWidth = typeof child.width === 'number' ? child.width : 360;
                          return Math.max(max, child.x + childWidth);
                      },
                      -Infinity
                  )
                : null;
            const sourceWidth = typeof sourceNode.width === 'number' ? sourceNode.width : 360;
            const baseX = existingChildren.length === 0
                ? sourceNode.x + sourceWidth / 2 - 180 - startOffset
                : (Number.isFinite(existingMaxRight)
                    ? existingMaxRight + 60
                    : sourceNode.x + sourceWidth / 2 - 180 + childSpacing);

            modelDescriptors.forEach((descriptor, index) => {
                const modelName = descriptor.label || descriptor.name.split('/').pop() || descriptor.name;
                const x = baseX + index * childSpacing;
                const y = baseY;

                const threadId = sourceNode.threadId || this.canvasState.activeThreadId;
                const placeholder = this.canvasState.createNode(`Generating with ${modelName}…`, x, y, descriptor.name, {
                    save: false,
                    threadId
                });
                placeholder.isStreaming = true;
                placeholder.streamingModel = descriptor.name;
                placeholder.streamingStartedAt = Date.now();

                this.canvasState.createConnection(sourceNode, placeholder);
                modelNodeMap.set(descriptor.key || descriptor.name, placeholder);
            });

            if (modelNodeMap.size > 0) {
                this.canvasState.saveToLocalStorage();
            }

            const onModelComplete = async (modelResult) => {
                completedModels++;

                const sourceStillExists = this.canvasState.nodes.find(n => n.id === sourceNode.id);
                if (!sourceStillExists) {
                    console.warn('⚠️ Source node was deleted during generation, skipping node creation');
                    return;
                }

                const modelLookupKey = modelResult.modelKey || modelResult.model;
                const targetNode = modelNodeMap.get(modelLookupKey);

                if (modelResult.success && modelResult.ideas && modelResult.ideas.length > 0) {
                    const finalText = modelResult.ideas[0] || '';
                    updateNodeContent(targetNode, finalText, { done: true });
                    successfulNodes++;
                    this.canvasState.saveToLocalStorage();

                    const modelName = modelResult.label || modelResult.model.split('/').pop() || modelResult.model;
                    this.uiManager.updateProgressModelStatus(modelLookupKey, {
                        status: 'success',
                        message: formatPreview(finalText),
                        ideaCount: 1
                    });
                    this.uiManager.showNotification(`✅ ${modelName} generated a response`, 'success', 2000);
                } else if (modelResult.success) {
                    const modelName = modelResult.label || modelResult.model.split('/').pop() || modelResult.model;
                    this.uiManager.showNotification(`❌ ${modelName} returned no ideas`, 'error', 3000);
                    this.uiManager.updateProgressModelStatus(modelLookupKey, {
                        status: 'error',
                        message: 'Model returned no ideas',
                        ideaCount: 0
                    });
                    if (targetNode) {
                        updateNodeContent(targetNode, 'No ideas generated.', { done: true });
                        this.canvasState.saveToLocalStorage();
                    }
                } else if (!modelResult.success) {
                    const modelName = modelResult.label || modelResult.model.split('/').pop() || modelResult.model;
                    this.uiManager.showNotification(`❌ ${modelName} failed: ${modelResult.errorMessage}`, 'error', 3000);
                    this.uiManager.updateProgressModelStatus(modelLookupKey, {
                        status: 'error',
                        message: formatPreview(modelResult.errorMessage || 'Generation failed'),
                        ideaCount: 0
                    });
                    if (targetNode) {
                        updateNodeContent(targetNode, `⚠️ ${modelResult.errorMessage || 'Generation failed'}`, { done: true });
                        this.canvasState.saveToLocalStorage();
                    }
                }

                this.uiManager.updateProgressNotification(completedModels, modelDescriptors.length, successfulNodes);
            };

            const onModelUpdate = async (update) => {
                const modelLookupKey = update.modelKey || update.model;
                const targetNode = modelNodeMap.get(modelLookupKey);
                if (!targetNode) {
                    return;
                }

                if (update.success && typeof update.full === 'string') {
                    updateNodeContent(targetNode, update.full, { done: Boolean(update.done) });
                    this.uiManager.updateProgressModelStatus(modelLookupKey, {
                        status: update.done ? 'pending' : 'pending',
                        message: formatPreview(update.full),
                        ideaCount: update.done && update.full.trim().length > 0 ? 1 : 0
                    });

                    if (update.done) {
                        this.canvasState.saveToLocalStorage();
                    }
                } else if (!update.success && update.done) {
                    const errorMessage = update.errorMessage || 'Generation failed';
                    updateNodeContent(targetNode, `⚠️ ${errorMessage}`, { done: true });
                    this.uiManager.updateProgressModelStatus(modelLookupKey, {
                        status: 'error',
                        message: formatPreview(errorMessage),
                        ideaCount: 0
                    });
                    this.canvasState.saveToLocalStorage();
                }
            };

            let modelResults;
            const systemPromptsByModel = modelDescriptors.reduce((acc, descriptor) => {
                const combinedPrompt = this.getCombinedSystemPrompt(descriptor);
                if (descriptor.key) {
                    acc[descriptor.key] = combinedPrompt;
                }
                if (descriptor.name && !Object.prototype.hasOwnProperty.call(acc, descriptor.name)) {
                    acc[descriptor.name] = combinedPrompt;
                }
                return acc;
            }, {});
            if (this.automanaged) {
                const userId = this.canvasState.authManager?.currentUser?.id || null;
                const sessionId = userId ? null : this.sessionId;
                
                const { generateAIIdeasMultipleAutomanaged } = await import('./aiService.js');
                modelResults = await generateAIIdeasMultipleAutomanaged(
                    sourceNode.text,
                    ancestorNodes,
                    modelDescriptors,
                    userId,
                    sessionId,
                    onModelComplete,
                    systemPromptsByModel,
                    onModelUpdate
                );
            } else {
                const { generateAIIdeasMultipleModels } = await import('./aiService.js');
                modelResults = await generateAIIdeasMultipleModels(
                    this.getTextProviders(),
                    sourceNode.text,
                    ancestorNodes,
                    modelDescriptors,
                    onModelComplete,
                    systemPromptsByModel,
                    onModelUpdate
                );
            }

            const successfulModels = modelResults.filter(r => r.success).length;
            const failedModels = modelResults.filter(r => !r.success).length;

            let summaryMessage;
            if (successfulNodes > 0) {
                summaryMessage = `🎉 Generation complete! ${successfulNodes} model(s) responded successfully`;
                if (failedModels > 0) {
                    summaryMessage += ` (${failedModels} model(s) failed)`;
                }
            } else {
                summaryMessage = `❌ No ideas generated. All ${models.length} model(s) failed.`;
            }

            this.uiManager.showNotification(summaryMessage, successfulNodes > 0 ? 'success' : 'error', 4000);

        } catch (error) {
            console.error('Error calling AI API:', error);
            const errorMessage = getErrorMessage(error);
            this.uiManager.showNotification(errorMessage, 'error');
        } finally {
            sourceNode.isGeneratingAI = false;
            this.uiManager.hideProgressNotification();
            this.uiManager.updateGenerateIdeasTooltip();
        }
    }
    
    // Image generation
    async generateImage() {
        if (this.canvasState.selectedNodes.length !== 1) {
            this.uiManager.showNotification('Please select a single node to generate an image.', 'warning');
            return;
        }
        
        const sourceNode = this.canvasState.selectedNodes[0];
        
        if (!this.replicateApiKey) {
            this.uiManager.showNotification('Please set your Replicate API key in the configuration.', 'error');
            return;
        }
        
        if (sourceNode.isGeneratingImage) {
            this.uiManager.showNotification('Image generation already in progress for this node...', 'info');
            return;
        }

        const allModels = (this.imageModels || '').split(',').map(m => m.trim()).filter(m => m.length > 0);
        const modelsToRun = allModels.filter(model => this.activeImageModels[model] !== false);

        if (modelsToRun.length === 0) {
            this.uiManager.showNotification('No active image models. Please enable models in the ⚙️ configuration menu.', 'warning');
            return;
        }

        try {
            sourceNode.isGeneratingImage = true;
            this.uiManager.updateGenerateIdeasTooltip();
            this.uiManager.showProgressNotification(`Starting image generation with ${modelsToRun.length} model(s)...`, modelsToRun);
            this.canvasState.saveState();

            let completedModels = 0;
            let totalImages = 0;
            const createdNodes = [];
            
            // Get selected aspect ratio from UIManager
            const aspectRatio = this.uiManager.getSelectedAspectRatio();

            const onModelComplete = async (result) => {
                completedModels++;
                
                const sourceStillExists = this.canvasState.nodes.find(n => n.id === sourceNode.id);
                if (!sourceStillExists) {
                    console.warn('⚠️ Source node was deleted during image generation, skipping node creation');
                    return;
                }
                
                if (result.success) {
                    const childSpacing = 450; // width of node (400) + 50 padding
                    const verticalOffset = 150;
                    const globalIndex = totalImages;

                    const x = sourceNode.x + (globalIndex - (modelsToRun.length - 1) / 2) * childSpacing;
                    const y = sourceNode.y + sourceNode.height + verticalOffset;
                    
                    const threadId = sourceNode.threadId || this.canvasState.activeThreadId;
                    const newNode = this.canvasState.createNode('', x, y, result.model, { threadId });
                    newNode.width = 320;
                    newNode.height = 240;
                    newNode.isMediaReference = true;
                    newNode.manuallyResized = true;
                    newNode.name = sourceNode.text;
                    newNode.generatedImageUrl = result.imageUrl;
                    createdNodes.push(newNode);
                    
                    this.canvasState.createConnection(sourceNode, newNode);
                    totalImages++;
                    
                    this.canvasState.saveToLocalStorage();
                    const modelName = result.model.split('/').pop() || result.model;
                    this.uiManager.showNotification(`✅ ${modelName} created an image`, 'success', 2000);
                } else {
                    const modelName = result.model.split('/').pop() || result.model;
                    this.uiManager.showNotification(`❌ ${modelName} failed: ${result.errorMessage}`, 'error', 3000);
                }
                
                this.uiManager.updateProgressNotification(completedModels, modelsToRun.length, totalImages);
            };

            const { generateImagesMultipleModels } = await import('./aiService.js');
            const results = await generateImagesMultipleModels(
                sourceNode.text, 
                this.replicateApiKey, 
                modelsToRun, 
                onModelComplete,
                { aspectRatio } // Pass aspect ratio as options
            );

            const successfulCount = results.filter(r => r.success).length;
            if (successfulCount > 0) {
                this.uiManager.showNotification(`🎉 Image generation complete! Created ${successfulCount} new image(s).`, 'success');
                this.canvasState.selectNodes(createdNodes);
            } else {
                this.uiManager.showNotification('❌ All image models failed to generate images.', 'error');
            }

        } catch (err) {
            this.uiManager.showNotification('Image generation failed: ' + err.message, 'error');
        } finally {
            sourceNode.isGeneratingImage = false;
            this.uiManager.hideProgressNotification();
            this.uiManager.updateGenerateIdeasTooltip();
        }
    }
    
    // Image upload
    uploadImage() {
        if (this.canvasState.selectedNodes.length !== 1) {
            this.uiManager.showNotification('Please select a single node to upload an image.', 'warning');
            return;
        }

        const uploadInput = document.getElementById('uploadImageInput');
        uploadInput.click();
    }

    async handleImageUpload(event) {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            this.uiManager.showNotification('Please select a valid image file.', 'error');
            return;
        }

        const maxSize = 200 * 1024 * 1024; // 200MB
        if (file.size > maxSize) {
            this.uiManager.showNotification('Image file too large. Please select an image smaller than 200MB.', 'error');
            return;
        }

        const node = this.canvasState.selectedNodes[0];

        try {
            node.isUploadingImage = true;
            this.uiManager.updateGenerateIdeasTooltip();
            this.uiManager.showNotification('Uploading image to cloud...', 'info', 3000);

            const imageUrl = await this.uploadToLitterbox(file);

            node.generatedImageUrl = imageUrl;
            this.canvasState.saveToLocalStorage();

            this.uiManager.showNotification('Image uploaded successfully and is now accessible online!', 'success');
        } catch (err) {
            this.uiManager.showNotification('Image upload failed: ' + err.message, 'error');
        } finally {
            node.isUploadingImage = false;
            this.uiManager.updateGenerateIdeasTooltip();
            event.target.value = '';
        }
    }

    async uploadToLitterbox(file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('reqtype', 'fileupload');
            formData.append('time', '72h');
            formData.append('fileToUpload', file);

            const xhr = new XMLHttpRequest();
            
            xhr.onload = function() {
                if (xhr.status === 200) {
                    const response = xhr.responseText.trim();
                    if (response.startsWith('https://')) {
                        resolve(response);
                    } else {
                        reject(new Error('Invalid response from upload service: ' + response));
                    }
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
                }
            };

            xhr.onerror = function() {
                reject(new Error('Network error during upload'));
            };

            xhr.ontimeout = function() {
                reject(new Error('Upload timed out'));
            };

            xhr.timeout = 60000;
            xhr.open('POST', 'https://litterbox.catbox.moe/resources/internals/api.php');
            xhr.send(formData);
        });
    }
    
    // Context image editing
    contextImage() {
        if (this.canvasState.selectedNodes.length !== 1) {
            this.uiManager.showNotification('Please select a single node to edit an image.', 'warning');
            return;
        }

        const node = this.canvasState.selectedNodes[0];

        if (!this.replicateApiKey) {
            this.uiManager.showNotification('Please set your Replicate API key in the configuration.', 'error');
            return;
        }

        if (!node.text || node.text.trim() === '' || node.text.trim() === 'New Node') {
            this.uiManager.showNotification('Please add text content to this node to use as editing instructions.', 'warning');
            return;
        }

        if (node.isGeneratingContext) {
            this.uiManager.showNotification('Image editing already in progress for this node...', 'info');
            return;
        }

        // Find connected image node
        const connectedImageNode = this.findConnectedImageNode(node);
        
        if (connectedImageNode) {
            this.generateContextImageFromConnected(node, connectedImageNode);
        } else {
            this.generateImageThenEdit(node);
        }
    }

    findConnectedImageNode(textNode) {
        const connections = this.canvasState.connections;
        const connectedNodeIds = new Set();
        
        // Find all nodes connected to this text node
        connections.forEach(conn => {
            if (conn.from === textNode.id) {
                connectedNodeIds.add(conn.to);
            }
            if (conn.to === textNode.id) {
                connectedNodeIds.add(conn.from);
            }
        });
        
        // Find the first connected image node
        return this.canvasState.nodes.find(node => 
            connectedNodeIds.has(node.id) && 
            node.isMediaReference && 
            node.generatedImageUrl
        );
    }

    async generateContextImageFromConnected(textNode, imageNode) {
        try {
            textNode.isGeneratingContext = true;
            this.uiManager.updateGenerateIdeasTooltip();
            this.uiManager.showNotification('Editing connected image with AI...', 'info', 3000);

            const editedImageUrl = await this.generateReplicateContextImage(
                imageNode.generatedImageUrl, 
                textNode.text, 
                this.replicateApiKey
            );

            // Create new image node with edited image
            const newNodeX = textNode.x + textNode.width + 50;
            const newNodeY = textNode.y;
            
            const connectedThreadId = textNode.threadId || imageNode.threadId || this.canvasState.activeThreadId;
            const newNode = this.canvasState.createNode('', newNodeX, newNodeY, null, { threadId: connectedThreadId });
            newNode.width = 320;
            newNode.height = 240;
            newNode.isMediaReference = true;
            newNode.manuallyResized = true;
            newNode.name = textNode.text;
            newNode.generatedImageUrl = editedImageUrl;
            
            this.canvasState.createConnection(textNode, newNode);
            this.canvasState.selectNodes(newNode);
            this.canvasState.saveToLocalStorage();

            this.uiManager.showNotification('Connected image edited successfully! Created new image node.', 'success');
        } catch (err) {
            this.uiManager.showNotification('Image editing failed: ' + err.message, 'error');
        } finally {
            textNode.isGeneratingContext = false;
            this.uiManager.updateGenerateIdeasTooltip();
        }
    }

    async generateImageThenEdit(node) {
        try {
            node.isGeneratingContext = true;
            this.uiManager.updateGenerateIdeasTooltip();
            this.uiManager.showNotification('Generating image and then editing it...', 'info', 5000);

            // First generate an image from the text
            const imageUrl = await this.generateReplicateImage(node.text, this.replicateApiKey);
            
            // Create initial image node
            const imageNodeX = node.x + node.width + 50;
            const imageNodeY = node.y;
            
            const baseThreadId = node.threadId || this.canvasState.activeThreadId;
            const imageNode = this.canvasState.createNode('', imageNodeX, imageNodeY, null, { threadId: baseThreadId });
            imageNode.width = 320;
            imageNode.height = 240;
            imageNode.isMediaReference = true;
            imageNode.manuallyResized = true;
            imageNode.name = node.text;
            imageNode.generatedImageUrl = imageUrl;
            
            this.canvasState.createConnection(node, imageNode);
            this.canvasState.saveToLocalStorage();

            // Now edit the generated image with the same text as instructions
            const editedImageUrl = await this.generateReplicateContextImage(
                imageUrl, 
                node.text, 
                this.replicateApiKey
            );

            // Create edited image node
            const editedNodeX = imageNodeX + 320 + 50;
            const editedNodeY = imageNodeY;
            
            const editedNode = this.canvasState.createNode('', editedNodeX, editedNodeY, null, { threadId: baseThreadId });
            editedNode.width = 320;
            editedNode.height = 240;
            editedNode.isMediaReference = true;
            editedNode.manuallyResized = true;
            editedNode.name = node.text;
            editedNode.generatedImageUrl = editedImageUrl;
            
            this.canvasState.createConnection(imageNode, editedNode);
            this.canvasState.selectNodes(editedNode);
            this.canvasState.saveToLocalStorage();

            this.uiManager.showNotification('Image generated and edited successfully!', 'success');
        } catch (err) {
            this.uiManager.showNotification('Image generation and editing failed: ' + err.message, 'error');
        } finally {
            node.isGeneratingContext = false;
            this.uiManager.updateGenerateIdeasTooltip();
        }
    }

    async generateReplicateImage(prompt, replicateApiKey) {
        const { generateReplicateImage } = await import('./aiService.js');
        return await generateReplicateImage(prompt, replicateApiKey);
    }

    async generateReplicateContextImage(imageUrl, prompt, replicateApiKey) {
        if (!replicateApiKey || !replicateApiKey.startsWith('r8_')) {
            throw new Error('Valid Replicate API key required.');
        }

        const submitRes = await fetch('/api/replicate-context', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                imageUrl, 
                prompt, 
                replicateApiKey,
                aspectRatio: 'match_input_image' // Use match_input_image for FLUX Kontext
            })
        });

        if (!submitRes.ok) {
            const err = await submitRes.text();
            throw new Error('Replicate context proxy error: ' + err);
        }

        const submitData = await submitRes.json();
        const getUrl = submitData.urls.get;

        let status = submitData.status;
        let outputUrl = null;
        let pollCount = 0;

        while (status !== 'succeeded' && status !== 'failed' && pollCount < 60) {
            await new Promise(r => setTimeout(r, 3000));
            
            const pollRes = await fetch('/api/replicate-poll', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ getUrl, replicateApiKey })
            });

            if (!pollRes.ok) {
                throw new Error('Polling error: ' + (await pollRes.text()));
            }

            const pollData = await pollRes.json();
            status = pollData.status;

            if (status === 'succeeded') {
                outputUrl = Array.isArray(pollData.output) ? pollData.output[0] : pollData.output;
            } else if (status === 'failed') {
                throw new Error('Image editing failed: ' + (pollData.error || 'Unknown error'));
            }

            pollCount++;
        }

        if (!outputUrl) {
            throw new Error('Image editing timed out or failed.');
        }

        return outputUrl;
    }
    
    // Video generation
    async generateVideo() {
        if (this.canvasState.selectedNodes.length !== 1) {
            this.uiManager.showNotification('Please select a single node to generate a video.', 'warning');
            return;
        }

        const node = this.canvasState.selectedNodes[0];

        if (!this.replicateApiKey) {
            this.uiManager.showNotification('Please set your Replicate API key in the configuration.', 'error');
            return;
        }

        if (!node.text || node.text.trim() === '' || node.text.trim() === 'New Node') {
            this.uiManager.showNotification('Please add text content to this node to use as video prompt.', 'warning');
            return;
        }

        if (node.isGeneratingVideo) {
            this.uiManager.showNotification('Video generation already in progress for this node...', 'info');
            return;
        }

        try {
            node.isGeneratingVideo = true;
            this.uiManager.updateGenerateIdeasTooltip();

            // Check for connected image nodes
            const connectedImageNode = this.findConnectedImageNode(node);
            const hasImage = connectedImageNode && connectedImageNode.generatedImageUrl;
            const modeText = hasImage ? 'image-to-video' : 'text-to-video';
            
            this.uiManager.showNotification(`Generating ${modeText} with AI...`, 'info', 3000);

            const videoUrl = await this.generateReplicateVideo(
                node.text,
                hasImage ? connectedImageNode.generatedImageUrl : null,
                this.replicateApiKey
            );

            // Create new video node instead of embedding in text node
            const newNodeX = node.x + node.width + 50;
            const newNodeY = node.y;
            
            const threadId = node.threadId || this.canvasState.activeThreadId;
            const videoNode = this.canvasState.createNode('', newNodeX, newNodeY, null, { threadId });
            videoNode.width = 480;
            videoNode.height = 320; // 270 (16:9 video) + 50 (header) = 320 total
            videoNode.isMediaReference = true;
            videoNode.manuallyResized = true;
            videoNode.name = node.text;
            videoNode.generatedVideoUrl = videoUrl;
            videoNode._headerHeight = 50; // Add header height for consistent UI with image nodes
            
            this.canvasState.createConnection(node, videoNode);
            this.canvasState.selectNodes(videoNode);
            this.canvasState.saveToLocalStorage();

            this.uiManager.showNotification(`Video generated successfully using ${modeText}!`, 'success');
        } catch (err) {
            this.uiManager.showNotification('Video generation failed: ' + err.message, 'error');
        } finally {
            node.isGeneratingVideo = false;
            this.uiManager.updateGenerateIdeasTooltip();
        }
    }

    async generateReplicateVideo(prompt, imageUrl, replicateApiKey) {
        if (!replicateApiKey || !replicateApiKey.startsWith('r8_')) {
            throw new Error('Valid Replicate API key required.');
        }

        const submitRes = await fetch('/api/replicate-video', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                prompt, 
                imageUrl,
                replicateApiKey 
            })
        });

        if (!submitRes.ok) {
            const err = await submitRes.text();
            throw new Error('Replicate video proxy error: ' + err);
        }

        const submitData = await submitRes.json();
        const getUrl = submitData.urls.get;

        let status = submitData.status;
        let outputUrl = null;
        let pollCount = 0;

        while (status !== 'succeeded' && status !== 'failed' && pollCount < 120) {
            await new Promise(r => setTimeout(r, 5000));
            
            const pollRes = await fetch('/api/replicate-poll', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ getUrl, replicateApiKey })
            });

            if (!pollRes.ok) {
                throw new Error('Polling error: ' + (await pollRes.text()));
            }

            const pollData = await pollRes.json();
            status = pollData.status;

            if (status === 'succeeded') {
                outputUrl = Array.isArray(pollData.output) ? pollData.output[0] : pollData.output;
            } else if (status === 'failed') {
                throw new Error('Video generation failed: ' + (pollData.error || 'Unknown error'));
            }

            pollCount++;
        }

        if (!outputUrl) {
            throw new Error('Video generation timed out or failed.');
        }

        return outputUrl;
    }
    
    // Configuration management
    saveApiConfig(config = {}) {
        const {
            textProviders = {},
            replicateApiKey = '',
            aiModels = '',
            activeModels = {},
            automanaged = false,
            imageModels = this.defaultImageModels,
            activeImageModels = {},
            systemPrompt = DEFAULT_SYSTEM_PROMPT,
            modelSystemPrompts = this.modelSystemPrompts
        } = config;

        const primary = textProviders.primary || {};
        const secondary = textProviders.secondary || {};

        const normalize = (value) => (typeof value === 'string' ? value.trim() : '');

        this.apiKey = normalize(primary.apiKey);
        this.baseURL = normalize(primary.baseURL);
        this.secondaryApiKey = normalize(secondary.apiKey);
        this.secondaryBaseURL = normalize(secondary.baseURL);
        this.replicateApiKey = normalize(replicateApiKey);
        this.aiModels = typeof aiModels === 'string' ? aiModels : '';
        this.activeModels = activeModels && typeof activeModels === 'object' ? { ...activeModels } : {};
        this.automanaged = automanaged === true;
        this.imageModels = typeof imageModels === 'string' ? imageModels : this.defaultImageModels;
        this.activeImageModels = activeImageModels && typeof activeImageModels === 'object' ? { ...activeImageModels } : {};
        this.systemPrompt = normalize(systemPrompt) || DEFAULT_SYSTEM_PROMPT;

        this.loadModelPromptConfig(modelSystemPrompts);
        const promptPayload = this.getModelSystemPromptConfig();
        this.modelSystemPrompts = promptPayload;

        const modeKey = this.getCurrentModeKey();
        const storageModeKey = this.normalizeModeKey(modeKey);

        if (this.automanaged) {
            localStorage.removeItem('ai_api_key');
            localStorage.removeItem('ai_base_url');
            localStorage.removeItem('ai_secondary_api_key');
            localStorage.removeItem('ai_secondary_base_url');

            const normalizedAutomanagedModels = this.normalizeModelList(this.aiModels);
            const fetchedDefaults = Array.isArray(this.automanagedFetchedModels)
                ? this.automanagedFetchedModels
                : [];

            let treatAsCustom = false;
            if (normalizedAutomanagedModels.length === 0) {
                treatAsCustom = false;
            } else if (fetchedDefaults.length === 0) {
                treatAsCustom = true;
            } else {
                const matchesFetchedDefaults = normalizedAutomanagedModels.length === fetchedDefaults.length
                    && normalizedAutomanagedModels.every((model, index) => model === fetchedDefaults[index]);
                treatAsCustom = !matchesFetchedDefaults;
            }

            if (treatAsCustom) {
                localStorage.setItem('ai_automanaged_custom_models', 'true');
                this.automanagedCustomModels = true;
            } else {
                localStorage.removeItem('ai_automanaged_custom_models');
                this.automanagedCustomModels = false;
            }
        } else {
            if (this.apiKey) {
                localStorage.setItem('ai_api_key', this.apiKey);
            } else {
                localStorage.removeItem('ai_api_key');
            }

            if (this.baseURL) {
                localStorage.setItem('ai_base_url', this.baseURL);
            } else {
                localStorage.removeItem('ai_base_url');
            }

            if (this.secondaryApiKey) {
                localStorage.setItem('ai_secondary_api_key', this.secondaryApiKey);
            } else {
                localStorage.removeItem('ai_secondary_api_key');
            }

            if (this.secondaryBaseURL) {
                localStorage.setItem('ai_secondary_base_url', this.secondaryBaseURL);
            } else {
                localStorage.removeItem('ai_secondary_base_url');
            }
        }

        if (this.replicateApiKey) {
            localStorage.setItem('replicate_api_key', this.replicateApiKey);
        } else {
            localStorage.removeItem('replicate_api_key');
        }

        localStorage.setItem('image_models', this.imageModels || '');
        localStorage.setItem('image_active_models', JSON.stringify(this.activeImageModels));
        localStorage.setItem('ai_automanaged', this.automanaged.toString());

        this.modeState[storageModeKey] = {
            aiModels: this.aiModels,
            activeModels: { ...this.activeModels },
            systemPrompt: this.systemPrompt,
            modelSystemPrompts: JSON.parse(JSON.stringify(promptPayload))
        };
        this.persistModeState(storageModeKey);
        this.applyModeState(storageModeKey);

        this.uiManager.updateModelSelectorUI();
        if (this.uiManager.updateImageModelSelectorUI) {
            this.uiManager.updateImageModelSelectorUI();
        }
        this.uiManager.updateApiConfigButton();

        const providerSummary = this.getProviderName();
        this.uiManager.showNotification(`Configuration saved successfully for ${providerSummary}`, 'success');
    }

    getProviderName() {
        if (this.automanaged) return 'Automanaged';

        const providers = [];

        if (this.apiKey && this.baseURL) {
            providers.push(resolveProviderName(this.baseURL));
        }

        if (this.secondaryApiKey && this.secondaryBaseURL) {
            const secondaryName = resolveProviderName(this.secondaryBaseURL);
            if (!providers.includes(secondaryName)) {
                providers.push(secondaryName);
            }
        }

        if (providers.length === 0) {
            return 'Custom API';
        }

        return providers.join(' + ');
    }

    getTextModelList() {
        // Always return the current text model list (automanaged or API key mode)
        return (this.aiModels || '')
            .split(',')
            .map(model => model.trim())
            .filter(model => model.length > 0);
    }

    getModelReferenceParts(identifier) {
        if (!identifier) {
            return { key: null, name: null };
        }

        if (typeof identifier === 'object') {
            const key = typeof identifier.key === 'string' ? identifier.key.trim() : '';
            const name = typeof identifier.name === 'string' ? identifier.name.trim() : '';
            return {
                key: key || null,
                name: name || null
            };
        }

        if (typeof identifier === 'string') {
            const normalized = identifier.trim();
            if (!normalized) {
                return { key: null, name: null };
            }
            return { key: normalized, name: normalized };
        }

        return { key: null, name: null };
    }

    getStoredActiveModelValue(identifier) {
        const { key, name } = this.getModelReferenceParts(identifier);

        if (key && Object.prototype.hasOwnProperty.call(this.activeModels, key)) {
            return this.activeModels[key];
        }

        if (name && Object.prototype.hasOwnProperty.call(this.activeModels, name)) {
            return this.activeModels[name];
        }

        return undefined;
    }

    isModelActive(identifier) {
        const storedValue = this.getStoredActiveModelValue(identifier);
        return storedValue !== false;
    }

    getTextModelDescriptors() {
        const models = this.getTextModelList();

        if (models.length === 0) {
            return [];
        }

        const totals = models.reduce((acc, model) => {
            acc[model] = (acc[model] || 0) + 1;
            return acc;
        }, {});

        const seen = {};

        return models.map((model, index) => {
            seen[model] = (seen[model] || 0) + 1;
            const occurrence = seen[model];
            const total = totals[model];
            const identifier = { name: model, key: `${model}__${index}` };
            const metadata = this.automanaged && this.automanagedModelLookup
                ? this.automanagedModelLookup[model]
                : null;
            const baseLabel = metadata?.name || model;
            const label = total > 1 ? `${baseLabel} #${occurrence}` : baseLabel;

            return {
                name: model,
                key: identifier.key,
                label,
                index,
                occurrence,
                totalOccurrences: total,
                isActive: this.isModelActive(identifier),
                hasCustomPrompt: this.hasModelSystemPrompt(identifier),
                provider: metadata?.provider || null,
                metadata: metadata || null
            };
        });
    }

    getActiveTextModelDescriptors() {
        const descriptors = this.getTextModelDescriptors();
        if (descriptors.length === 0) {
            return [];
        }

        const active = descriptors.filter(descriptor => descriptor.isActive);
        if (active.length > 0) {
            return active;
        }

        return descriptors.length > 0 ? [descriptors[0]] : [];
    }

    getPrimaryTextModel() {
        const activeDescriptors = this.getActiveTextModelDescriptors();
        if (activeDescriptors.length > 0) {
            return activeDescriptors[0].name;
        }

        const descriptors = this.getTextModelDescriptors({ useDemo: false });
        return descriptors.length > 0 ? descriptors[0].name : null;
    }

    getActiveTextModels() {
        return this.getActiveTextModelDescriptors().map(descriptor => descriptor.name);
    }

    setModelActive(model, isActive, mode = this.getCurrentModeKey()) {
        const normalizedMode = this.normalizeModeKey(mode);
        const { key, name } = this.getModelReferenceParts(model);
        if (!key && !name) {
            return;
        }

        const storageKey = key || name;
        const targetState = this.cloneModeState(normalizedMode);
        const activeMap = targetState.activeModels || {};
        activeMap[storageKey] = isActive;

        if (key && name && key !== name && Object.prototype.hasOwnProperty.call(activeMap, name)) {
            delete activeMap[name];
        }

        targetState.activeModels = activeMap;
        this.modeState[normalizedMode] = {
            ...targetState,
            activeModels: { ...activeMap }
        };

        if (normalizedMode === this.getCurrentModeKey()) {
            this.activeModels = { ...activeMap };
        }

        this.persistModeState(normalizedMode);
    }

    normalizePromptValue(value) {
        return typeof value === 'string' ? value.trim() : '';
    }

    getModelSystemPrompt(model) {
        const { key, name } = this.getModelReferenceParts(model);
        if (!key && !name) {
            return '';
        }

        if (key && typeof this.modelPromptOverrides?.[key] === 'string') {
            return this.modelPromptOverrides[key];
        }

        if (key && this.clearedModelPromptKeys.has(key)) {
            return '';
        }

        if (name && typeof this.legacyModelPrompts?.[name] === 'string') {
            return this.legacyModelPrompts[name];
        }

        return '';
    }

    hasModelSystemPrompt(model) {
        return this.getModelSystemPrompt(model).trim().length > 0;
    }

    setModelSystemPrompt(model, prompt) {
        const { key, name } = this.getModelReferenceParts(model);
        if (!key && !name) {
            return '';
        }

        const normalizedPrompt = this.normalizePromptValue(prompt);

        if (normalizedPrompt) {
            if (key) {
                this.modelPromptOverrides[key] = normalizedPrompt;
                this.clearedModelPromptKeys.delete(key);
            } else if (name) {
                this.modelPromptOverrides[name] = normalizedPrompt;
                this.clearedModelPromptKeys.delete(name);
            }

            if (name && this.legacyModelPrompts[name]) {
                delete this.legacyModelPrompts[name];
            }
        } else {
            if (key && Object.prototype.hasOwnProperty.call(this.modelPromptOverrides, key)) {
                delete this.modelPromptOverrides[key];
            }

            if (key) {
                this.clearedModelPromptKeys.add(key);
            } else if (name) {
                this.clearedModelPromptKeys.add(name);
            }
        }

        this.updateModelPromptStorage();

        return this.getModelSystemPrompt({ key, name });
    }

    getCombinedSystemPrompt(model) {
        const globalPrompt = typeof this.systemPrompt === 'string' ? this.systemPrompt.trim() : '';
        const modelPrompt = this.getModelSystemPrompt(model).trim();

        if (globalPrompt && modelPrompt) {
            return `${globalPrompt}\n\n${modelPrompt}`;
        }

        return modelPrompt || globalPrompt || '';
    }

    getModelSystemPromptConfig() {
        const payload = this.buildModelPromptStoragePayload();
        try {
            return JSON.parse(JSON.stringify(payload));
        } catch (error) {
            return { ...payload };
        }
    }

    loadModelPromptConfig(rawConfig) {
        this.modelPromptOverrides = {};
        this.legacyModelPrompts = {};
        this.clearedModelPromptKeys = new Set();

        if (!rawConfig || typeof rawConfig !== 'object' || Array.isArray(rawConfig)) {
            return;
        }

        const processEntries = (source, target) => {
            if (!source || typeof source !== 'object') {
                return;
            }

            Object.entries(source).forEach(([entryKey, entryValue]) => {
                if (typeof entryKey !== 'string') {
                    return;
                }

                const normalizedKey = entryKey.trim();
                if (!normalizedKey) {
                    return;
                }

                const normalizedValue = this.normalizePromptValue(entryValue);
                if (normalizedValue) {
                    target[normalizedKey] = normalizedValue;
                }
            });
        };

        const byKey = rawConfig.__byKey;
        const byName = rawConfig.__byName;
        const cleared = rawConfig.__cleared;

        if (byKey || byName || cleared) {
            processEntries(byKey, this.modelPromptOverrides);
            processEntries(byName, this.legacyModelPrompts);

            if (Array.isArray(cleared)) {
                cleared.forEach(value => {
                    if (typeof value === 'string') {
                        const normalized = value.trim();
                        if (normalized) {
                            this.clearedModelPromptKeys.add(normalized);
                        }
                    }
                });
            }
        } else {
            processEntries(rawConfig, this.legacyModelPrompts);
        }
    }

    buildModelPromptStoragePayload() {
        const payload = {};
        const overrides = this.modelPromptOverrides && typeof this.modelPromptOverrides === 'object'
            ? this.modelPromptOverrides
            : {};
        const legacy = this.legacyModelPrompts && typeof this.legacyModelPrompts === 'object'
            ? this.legacyModelPrompts
            : {};

        const overrideKeys = Object.keys(overrides);
        if (overrideKeys.length > 0) {
            payload.__byKey = overrideKeys.reduce((acc, key) => {
                acc[key] = overrides[key];
                return acc;
            }, {});
        }

        const legacyKeys = Object.keys(legacy);
        if (legacyKeys.length > 0) {
            payload.__byName = legacyKeys.reduce((acc, key) => {
                acc[key] = legacy[key];
                return acc;
            }, {});
        }

        if (this.clearedModelPromptKeys && this.clearedModelPromptKeys.size > 0) {
            payload.__cleared = Array.from(this.clearedModelPromptKeys);
        }

        return payload;
    }

    updateModelPromptStorage({ persist = true, mode = this.getCurrentModeKey() } = {}) {
        const normalizedMode = this.normalizeModeKey(mode);
        const payload = this.buildModelPromptStoragePayload();
        this.modelSystemPrompts = payload;

        const clonedPayload = JSON.parse(JSON.stringify(payload));
        const existingState = this.modeState?.[normalizedMode] || this.createEmptyModeState(normalizedMode);
        this.modeState[normalizedMode] = {
            ...existingState,
            modelSystemPrompts: clonedPayload
        };

        if (persist) {
            this.persistModeState(normalizedMode);
        }
    }

    hasAnyTextProvider() {
        const hasPrimary = this.apiKey && this.baseURL;
        const hasSecondary = this.secondaryApiKey && this.secondaryBaseURL;
        return Boolean(hasPrimary || hasSecondary);
    }

    getTextProviders() {
        return {
            primary: {
                apiKey: this.apiKey || '',
                baseURL: this.baseURL || ''
            },
            secondary: {
                apiKey: this.secondaryApiKey || '',
                baseURL: this.secondaryBaseURL || ''
            }
        };
    }

    async generateChatResponse(chatHistory, handlers = {}) {
        if (!Array.isArray(chatHistory) || chatHistory.length === 0) {
            throw new Error('Please provide a message to start the conversation.');
        }

        const sanitizedHistory = chatHistory
            .filter(entry => entry && entry.content && entry.content.trim().length > 0)
            .map(entry => ({
                role: entry.role === 'system' ? 'system' : entry.role,
                content: entry.content.trim()
            }));

        if (sanitizedHistory.length === 0) {
            throw new Error('Conversation history is empty.');
        }

        const activeDescriptors = this.getActiveTextModelDescriptors();
        if (!activeDescriptors || activeDescriptors.length === 0) {
            throw new Error('No active text models configured.');
        }

        const { onModelUpdate = null, onModelComplete = null } = handlers || {};

        if (this.automanaged) {
            return this.generateChatResponseAutomanaged(sanitizedHistory, activeDescriptors, { onModelUpdate, onModelComplete });
        }

        if (!this.hasAnyTextProvider()) {
            throw new Error('Please configure at least one API provider to use chat mode.');
        }

        const providerConfig = this.getTextProviders();
        const responseTasks = activeDescriptors.map(async (descriptor) => {
            const { name, key, label } = descriptor;
            let providerKey = null;
            const systemPrompt = this.getCombinedSystemPrompt(descriptor);

            const emitUpdate = async (update) => {
                if (typeof onModelUpdate === 'function') {
                    try {
                        await onModelUpdate({
                            model: name,
                            modelKey: key,
                            label,
                            provider: providerKey,
                            ...update
                        });
                    } catch (callbackError) {
                        console.error(`❌ Chat stream callback error for ${name}:`, callbackError);
                    }
                }
            };

            try {
                const { apiKey, baseURL, providerKey: resolvedKey } = resolveModelCredentials(name, providerConfig);
                providerKey = resolvedKey;
                const content = await streamChatCompletion({
                    apiKey,
                    baseURL,
                    messages: sanitizedHistory,
                    model: name,
                    systemPrompt,
                    onToken: async (update) => {
                        await emitUpdate({ ...update, success: true });
                    }
                });

                const trimmedContent = typeof content === 'string' ? content.trim() : '';
                const success = trimmedContent.length > 0;
                const result = {
                    model: name,
                    modelKey: key,
                    label,
                    content: trimmedContent,
                    success,
                    provider: providerKey
                };

                if (!success) {
                    result.error = true;
                    result.errorMessage = 'Model returned no content';
                }

                if (typeof onModelComplete === 'function') {
                    try {
                        await onModelComplete(result);
                    } catch (callbackError) {
                        console.error(`❌ Chat completion callback error for ${name}:`, callbackError);
                    }
                }

                return result;
            } catch (error) {
                console.error(`❌ Error with chat model ${name}:`, error.message);
                await emitUpdate({
                    success: false,
                    done: true,
                    error: true,
                    errorMessage: error?.message || 'Unknown error'
                });

                const result = {
                    model: name,
                    modelKey: key,
                    label,
                    content: error?.message || 'Unknown error',
                    success: false,
                    error,
                    provider: providerKey
                };

                if (typeof onModelComplete === 'function') {
                    try {
                        await onModelComplete(result);
                    } catch (callbackError) {
                        console.error(`❌ Chat completion callback error for ${name}:`, callbackError);
                    }
                }

                return result;
            }
        });

        return Promise.all(responseTasks);
    }

    async generateChatResponseAutomanaged(chatHistory, models, callbacks = {}) {
        const userMessages = chatHistory.filter(message => message.role === 'user');

        if (userMessages.length === 0) {
            throw new Error('Send a message to begin the chat.');
        }

        const latestMessage = userMessages[userMessages.length - 1];
        const previousMessages = userMessages
            .slice(0, -1)
            .map(message => ({ text: message.content }));

        const { onModelUpdate = null, onModelComplete = null } = callbacks || {};
        
        const userId = this.canvasState.authManager?.currentUser?.id || null;
        const sessionId = userId ? null : this.sessionId;

        const responseTasks = models.map(async (descriptor) => {
            const { name: modelName, key, label } = typeof descriptor === 'string'
                ? { name: descriptor, key: `${descriptor}__0`, label: descriptor }
                : descriptor;
            const promptIdentifier = typeof descriptor === 'string' ? { name: modelName, key } : descriptor;
            try {
                const systemPrompt = this.getCombinedSystemPrompt(promptIdentifier);
                const { generateAIIdeasAutomanaged } = await import('./aiService.js');
                
                const ideas = await generateAIIdeasAutomanaged(
                    latestMessage.content,
                    previousMessages,
                    userId,
                    sessionId,
                    systemPrompt,
                    async (update) => {
                        if (typeof onModelUpdate === 'function') {
                            try {
                                await onModelUpdate({
                                    model: modelName,
                                    modelKey: key,
                                    label,
                                    provider: 'automanaged',
                                    ...update
                                });
                            } catch (callbackError) {
                                console.error(`❌ Chat automanaged stream callback error for ${modelName}:`, callbackError);
                            }
                        }
                    },
                    modelName
                );
                const content = ideas?.[0] || 'I had trouble generating a response in automanaged mode.';
                const result = {
                    model: modelName,
                    modelKey: key,
                    label,
                    content,
                    success: true,
                    provider: 'automanaged'
                };

                if (typeof onModelComplete === 'function') {
                    try {
                        await onModelComplete(result);
                    } catch (callbackError) {
                        console.error(`❌ Chat automanaged completion callback error for ${modelName}:`, callbackError);
                    }
                }

                return result;
            } catch (error) {
                if (typeof onModelUpdate === 'function') {
                    try {
                        await onModelUpdate({
                            model: modelName,
                            modelKey: key,
                            label,
                            provider: 'automanaged',
                            success: false,
                            done: true,
                            error: true,
                            errorMessage: error?.message || 'Unknown error'
                        });
                    } catch (callbackError) {
                        console.error(`❌ Chat automanaged stream callback error for ${modelName}:`, callbackError);
                    }
                }

                if (typeof onModelComplete === 'function') {
                    try {
                        await onModelComplete({
                            model: modelName,
                            modelKey: key,
                            label,
                            content: error?.message || 'Unknown error',
                            success: false,
                            error,
                            provider: 'automanaged'
                        });
                    } catch (callbackError) {
                        console.error(`❌ Chat automanaged completion callback error for ${modelName}:`, callbackError);
                    }
                }

                return {
                    model: modelName,
                    modelKey: key,
                    label,
                    content: error?.message || 'Unknown error',
                    success: false,
                    error,
                    provider: 'automanaged'
                };
            }
        });

        return Promise.all(responseTasks);
    }

    // Generate with reference images using RunwayML Gen-4
    async generateWithReference() {
        if (this.canvasState.selectedNodes.length !== 1) {
            this.uiManager.showNotification('Please select a single text node to generate with references.', 'warning');
            return;
        }
        
        const textNode = this.canvasState.selectedNodes[0];
        
        if (!this.replicateApiKey) {
            this.uiManager.showNotification('Please set your Replicate API key in the configuration.', 'error');
            return;
        }
        
        if (!textNode.text || textNode.text.trim() === '' || textNode.text.trim() === 'New Node') {
            this.uiManager.showNotification('Please add text content to this node to use as generation prompt.', 'warning');
            return;
        }
        
        if (textNode.isGeneratingReference) {
            this.uiManager.showNotification('Reference generation already in progress for this node...', 'info');
            return;
        }
        
        // Find all connected image nodes
        const connectedImageNodes = this.findConnectedImageNodes(textNode);
        
        if (connectedImageNodes.length === 0) {
            this.uiManager.showNotification('No connected image nodes found. Connect some image nodes to use as references.', 'warning');
            return;
        }
        
        if (connectedImageNodes.length > 3) {
            this.uiManager.showNotification('Too many connected images. Gen-4 supports up to 3 reference images. Using the first 3.', 'warning');
        }
        
        try {
            textNode.isGeneratingReference = true;
            this.uiManager.updateGenerateIdeasTooltip();
            
            const referenceCount = Math.min(connectedImageNodes.length, 3);
            this.uiManager.showNotification(`Generating image with ${referenceCount} reference(s) using RunwayML Gen-4...`, 'info', 5000);
            
            const referenceImages = connectedImageNodes.slice(0, 3).map(node => node.generatedImageUrl);
            const referenceTags = connectedImageNodes.slice(0, 3).map(node => {
                // Generate valid tag from node name or create default
                const baseName = (node.name && node.name.trim()) ? node.name.trim() : `ref${Math.floor(Math.random() * 1000)}`;
                // Make tag alphanumeric and 3-15 characters
                let tag = baseName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
                if (tag.length < 3) tag = tag + 'ref';
                if (tag.length > 15) tag = tag.substring(0, 15);
                if (!/^[a-zA-Z]/.test(tag)) tag = 'ref' + tag.substring(0, 12);
                return tag;
            });
            
            // Get selected aspect ratio from UIManager
            const aspectRatio = this.uiManager.getSelectedAspectRatio();
            
            const imageUrl = await this.generateRunwayGen4Image(
                textNode.text,
                referenceImages,
                referenceTags,
                this.replicateApiKey,
                aspectRatio // Pass aspect ratio
            );
            
            // Create new image node with generated result
            const newNodeX = textNode.x + textNode.width + 50;
            const newNodeY = textNode.y;
            
            const threadId = textNode.threadId || this.canvasState.activeThreadId;
            const newNode = this.canvasState.createNode('', newNodeX, newNodeY, null, { threadId });
            newNode.width = 320;
            newNode.height = 240;
            newNode.isMediaReference = true;
            newNode.manuallyResized = true;
            newNode.name = textNode.text;
            newNode.generatedImageUrl = imageUrl;
            
            this.canvasState.createConnection(textNode, newNode);
            this.canvasState.selectNodes(newNode);
            this.canvasState.saveToLocalStorage();
            
            this.uiManager.showNotification(`Image generated successfully with ${referenceCount} reference(s)!`, 'success');
        } catch (err) {
            this.uiManager.showNotification('Reference generation failed: ' + err.message, 'error');
        } finally {
            textNode.isGeneratingReference = false;
            this.uiManager.updateGenerateIdeasTooltip();
        }
    }
    
    findConnectedImageNodes(textNode) {
        const connections = this.canvasState.connections;
        const inputImageNodeIds = new Set();
        
        // Find only image nodes that are inputs to this text node
        // (connections FROM image nodes TO this text node)
        connections.forEach(conn => {
            if (conn.to === textNode.id) {
                inputImageNodeIds.add(conn.from);
            }
        });
        
        // Return only input image nodes (not output/generated ones)
        return this.canvasState.nodes.filter(node => 
            inputImageNodeIds.has(node.id) && 
            node.isMediaReference && 
            node.generatedImageUrl
        );
    }
    
    async generateRunwayGen4Image(prompt, referenceImages, referenceTags, replicateApiKey, aspectRatio) {
        if (!replicateApiKey || !replicateApiKey.startsWith('r8_')) {
            throw new Error('Valid Replicate API key required.');
        }
        
        const submitRes = await fetch('/api/replicate-gen4', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                prompt,
                referenceImages,
                referenceTags,
                replicateApiKey,
                aspectRatio
            })
        });
        
        if (!submitRes.ok) {
            const err = await submitRes.text();
            throw new Error('RunwayML Gen-4 proxy error: ' + err);
        }
        
        const submitData = await submitRes.json();
        const getUrl = submitData.urls.get;
        
        let status = submitData.status;
        let outputUrl = null;
        let pollCount = 0;
        
        while (status !== 'succeeded' && status !== 'failed' && pollCount < 60) {
            await new Promise(r => setTimeout(r, 5000));
            
            const pollRes = await fetch('/api/replicate-poll', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ getUrl, replicateApiKey })
            });
            
            if (!pollRes.ok) {
                throw new Error('Polling error: ' + (await pollRes.text()));
            }
            
            const pollData = await pollRes.json();
            status = pollData.status;
            
            if (status === 'succeeded') {
                outputUrl = Array.isArray(pollData.output) ? pollData.output[0] : pollData.output;
            } else if (status === 'failed') {
                throw new Error('Gen-4 generation failed: ' + (pollData.error || 'Unknown error'));
            }
            
            pollCount++;
        }
        
        if (!outputUrl) {
            throw new Error('Gen-4 generation timed out or failed.');
        }
        
        return outputUrl;
    }
} 
